import { Button, Text, VStack } from "@chakra-ui/react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { memo, useCallback, useState } from "react"

import { ConfirmDialog } from "@/components/molecules/ConfirmDialog"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { api } from "@/services/api"
import { handleError } from "@/utils"

const DeactivateConfirmation = memo(() => {
  const [isOpen, setIsOpen] = useState(false)
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const { logout } = useAuth()

  const mutation = useMutation({
    mutationFn: async () => {
      await api.users.deactivateCurrentUserApiV1UsersMeDeactivatePost()
    },
    onSuccess: () => {
      showSuccessToast("Your account has been deactivated successfully")
      logout()
      queryClient.invalidateQueries({ queryKey: ["currentUser"] })
    },
    onError: (err: Error) => {
      handleError(err)
    },
  })

  const handleConfirm = useCallback(async () => {
    await mutation.mutateAsync()
  }, [mutation])

  return (
    <>
      <Button
        variant="solid"
        colorPalette="orange"
        mt={4}
        onClick={() => setIsOpen(true)}
      >
        Deactivate
      </Button>

      <ConfirmDialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onConfirm={handleConfirm}
        title="Deactivate Account"
        confirmText="Deactivate Account"
        confirmColorScheme="orange"
        isLoading={mutation.isPending}
        message={
          <VStack gap={3} align="start">
            <Text>
              Your account will be <strong>temporarily deactivated</strong>. You
              won't be able to log in, but your data will be preserved. An
              administrator can reactivate your account if needed.
            </Text>
            <Text color="gray.600" fontSize="sm">
              This action can be reversed by an administrator.
            </Text>
          </VStack>
        }
      />
    </>
  )
})

DeactivateConfirmation.displayName = "DeactivateConfirmation"

export default DeactivateConfirmation
